import { Component, computed, effect, inject, OnInit, signal, ChangeDetectionStrategy } from '@angular/core';
import { NavigationCancel, NavigationEnd, NavigationError, NavigationSkipped, NavigationStart, Router, RouterOutlet } from '@angular/router';
import { ToasterComponent } from './shared/components/toaster/toaster.component';
import { ThemeService } from './core/services/theme.service';
import { LoaderService } from './core/services/loader.service';
import { NgxSonnerToaster } from 'ngx-sonner';
import { GlobalLoaderComponent } from './shared/components/loader-new/loader.component';
import { CredeeLoaderComponent } from './pages/public/credee-demo/components/credee-loader.component';
import { AccessRefreshService } from './core/services/access-refresh.service';

@Component({
  selector: 'app-root',
  standalone: true,
  imports: [RouterOutlet, ToasterComponent, NgxSonnerToaster, GlobalLoaderComponent, CredeeLoaderComponent],
  changeDetection: ChangeDetectionStrategy.Eager,
  template: ` 
  <div
    class="relative bg-gradient-to-br from-slate-50 via-slate-100 to-slate-50 dark:from-surface-0 dark:via-surface-0 dark:to-surface-0 text-slate-700 dark:text-ink-soft transition-all duration-300"
  >
    <!-- <div class="fixed inset-0 overflow-hidden pointer-events-none">
      <div class="fixed inset-0 overflow-hidden pointer-events-none">
        <div
          class="absolute w-[600px] h-[600px] rounded-full blur-[130px] transition-all duration-1000
             bg-purple-400/15 dark:bg-purple-600/25"
          style="top: 10%; left: 5%;"
        ></div>
        <div
          class="absolute w-[500px] h-[500px] rounded-full blur-[110px] transition-all duration-1000
             bg-indigo-400/12 dark:bg-indigo-600/20"
          style="bottom: 10%; right: 10%;"
        ></div>
        <div
          class="absolute w-[450px] h-[450px] rounded-full blur-[120px] transition-all duration-1000
             bg-violet-400/12 dark:bg-violet-600/20"
          style="top: 40%; right: 5%;"
        ></div>
      </div>
    </div> -->
  <main class="w-full h-full">
  <router-outlet />
</main>

@if (isCredeeRoute()) {
  <app-credee-loader />
} @else {
  <app-global-loader />
}
<app-toaster />
<!-- Toast Container -->
    <ngx-sonner-toaster 
      position="bottom-right" 
      [visibleToasts]="3"
      [theme]="themeService.theme() === 'dark' ? 'dark' : 'light'"
      [closeButton]="true"
      [richColors]="true"
      [duration]="4000"
      [expand]="false" />

  </div>`,
})
export class AppComponent implements OnInit {
  title = 'prism-app';

  protected themeService = inject(ThemeService);
  private router = inject(Router);
  public loader = inject(LoaderService);
  private navigationLoaderShown = false;

  /**
   * Current URL, kept in sync with navigation so the loader can adapt per-route.
   * Seeded from the real browser URL so a direct/first load (before the router's
   * initial navigation resolves) already resolves the right loader.
   */
  private readonly currentUrl = signal<string>(
    typeof window !== 'undefined' ? window.location.pathname + window.location.search : this.router.url,
  );
  /** The Credee demos swap in their own branded loader; the rest of the app keeps
   *  the default. Covers `/credee-demo*` AND `/instant-demo-v2` — should any
   *  stray request flip the loader on there, it must never be the Prism one. */
  protected readonly isCredeeRoute = computed(() =>
    this.currentUrl().includes('credee-demo') || this.currentUrl().includes('instant-demo-v2') || this.currentUrl().includes('instant-demo'));

  constructor() {
    inject(AccessRefreshService).start();

    this.router.events.subscribe(event => {

      if (event instanceof NavigationStart) {
        // Update the route as navigation begins, so the loader shown *during* this
        // navigation (and the lazy-chunk load) already matches the destination.
        this.currentUrl.set(event.url);

        // Query-only updates (e.g. Denefits urgent/vip filters) should use in-page loaders only.
        if (this.isSamePathNavigation(event.url)) {
          this.navigationLoaderShown = false;
          return;
        }

        // The LiveKit demo (`/instant-demo-v2`) owns its busy states
        // (branded wordmark loader, rail skeletons) — never cover its screens
        // with the global navigation loader.
        if (event.url.includes('instant-demo-v2')) {
          this.navigationLoaderShown = false;
          return;
        }

        let loader_type: 'modal' | 'spinner' | 'none' = 'modal';
        if (event.url.includes('chatbot')) {
          loader_type = 'spinner';
        }
        this.navigationLoaderShown = true;
        this.loader.show({ message: '', type: loader_type });
        // console.debug('[LOADER-DBG] nav show', event.url); // TEMP
      }

      // Hide on ANY terminal navigation event. Previously only End/Error were
      // handled, so a NavigationCancel (guard/redirect) or NavigationSkipped left
      // the loader stuck `show:true` on top of the current page — a live call could
      // stay covered while it kept running underneath.
      if (
        event instanceof NavigationEnd ||
        event instanceof NavigationError ||
        event instanceof NavigationCancel ||
        event instanceof NavigationSkipped
      ) {
        this.currentUrl.set(this.router.url);
        if (this.navigationLoaderShown) {
          this.loader.hide();
          this.navigationLoaderShown = false;
          // console.debug('[LOADER-DBG] nav hide', (event as { constructor: { name: string } }).constructor.name); // TEMP
        }
      }
    });

    effect(() => {
    });
  }

  ngOnInit() {
    this.applyAndroidPlatformClass();
  }

  /**
   * Adds an `is-android` class on <html> only when the app is running on an
   * Android device. This lets us scope a few mobile-only style adjustments
   * (e.g. bottom-navigation overlap padding) to Android without touching
   * any existing iOS / desktop behaviour.
   */
  private applyAndroidPlatformClass(): void {
    if (typeof navigator === 'undefined' || typeof document === 'undefined') {
      return;
    }
    const ua = navigator.userAgent || '';
    const isAndroid = /android/i.test(ua);
    if (isAndroid) {
      document.documentElement.classList.add('is-android');
    }
  }

  /** True when navigation only changes query/hash on the current route (not a real route change). */
  private isSamePathNavigation(url: string): boolean {
    const strip = (value: string) => value.split('?')[0].split('#')[0];
    const current = strip(this.router.url);
    const next = strip(url);
    return !!current && !!next && current === next;
  }
}
